import React, { useRef, useState, useEffect } from 'react';

interface Channel {
  id: string;
  label: string;
  sublabel: string;
  streamUrl: string;
  color: string;
}

const CHANNELS: Channel[] = [
  {
    id: 'sheriff',
    label: 'PIMA CO SHERIFF',
    sublabel: 'Oro Valley PD + PCSD',
    streamUrl: 'https://broadcastify.cdnstream1.com/30258',
    color: '#3b82f6',
  },
  {
    id: 'ems',
    label: 'PIMA PUBLIC SAFETY',
    sublabel: 'Green Valley EMS/Fire',
    streamUrl: 'https://broadcastify.cdnstream1.com/45739',
    color: '#22c55e',
  },
];

export default function RadioPlayer() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [open, setOpen] = useState(false);
  const [activeChannel, setActiveChannel] = useState<Channel | null>(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Apply volume/mute to audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.muted = muted;
    }
  }, [volume, muted]);

  const selectChannel = (ch: Channel) => {
    const audio = audioRef.current;
    if (!audio) return;
    setError(null);

    if (activeChannel?.id === ch.id && playing) {
      // Pause same channel
      audio.pause();
      audio.src = '';
      setPlaying(false);
      setActiveChannel(null);
      return;
    }

    // Switch or start
    audio.pause();
    audio.src = ch.streamUrl;
    setActiveChannel(ch);
    setLoading(true);
    setPlaying(false);

    audio.load();
    audio.play()
      .then(() => { setPlaying(true); setLoading(false); })
      .catch((e) => {
        setError('Stream unavailable');
        setLoading(false);
        setPlaying(false);
        console.warn('[RadioPlayer]', e);
      });
  };

  const toggleMute = () => setMuted(m => !m);

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (v > 0) setMuted(false);
  };

  return (
    <>
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onError={() => { setError('Stream unavailable'); setLoading(false); setPlaying(false); }}
        onWaiting={() => setLoading(true)}
        onPlaying={() => { setPlaying(true); setLoading(false); setError(null); }}
        onStalled={() => setLoading(true)}
      />

      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Live Radio"
        style={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          zIndex: 500,
          width: 44,
          height: 44,
          borderRadius: '50%',
          border: playing
            ? '2px solid ' + (activeChannel?.color ?? '#fbbf24')
            : '2px solid rgba(251,191,36,0.4)',
          backgroundColor: open ? 'rgba(251,191,36,0.15)' : 'rgba(0,0,0,0.75)',
          backdropFilter: 'blur(8px)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s',
          boxShadow: playing
            ? `0 0 12px ${activeChannel?.color ?? '#fbbf24'}60`
            : '0 2px 8px rgba(0,0,0,0.5)',
        }}
      >
        {/* Radio wave icon */}
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={playing ? (activeChannel?.color ?? '#fbbf24') : '#fbbf2499'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
          <path d="M21 19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        {playing && (
          <span style={{
            position: 'absolute',
            top: 0, right: 0,
            width: 10, height: 10,
            borderRadius: '50%',
            backgroundColor: activeChannel?.color ?? '#22c55e',
            border: '2px solid #000',
            animation: 'ping 1.5s ease-in-out infinite',
          }} />
        )}
      </button>

      {/* Collapsible panel */}
      {open && (
        <div style={{
          position: 'absolute',
          bottom: 68,
          right: 16,
          zIndex: 500,
          width: 260,
          backgroundColor: 'rgba(0,0,0,0.92)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(251,191,36,0.25)',
          borderRadius: 10,
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          fontFamily: '"Orbitron", monospace',
        }}>
          {/* Header */}
          <div style={{
            padding: '8px 12px',
            borderBottom: '1px solid rgba(251,191,36,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {playing && (
                <span style={{
                  display: 'inline-flex',
                  width: 8, height: 8,
                  borderRadius: '50%',
                  backgroundColor: activeChannel?.color ?? '#22c55e',
                  boxShadow: `0 0 6px ${activeChannel?.color ?? '#22c55e'}`,
                  animation: 'ping 1.5s ease-in-out infinite',
                }} />
              )}
              <span style={{ color: '#fbbf24', fontSize: 10, letterSpacing: '0.12em', fontWeight: 700 }}>
                LIVE RADIO
              </span>
            </div>
            {playing && (
              <span style={{ color: activeChannel?.color ?? '#22c55e', fontSize: 9, letterSpacing: '0.08em' }}>
                {loading ? 'BUFFERING…' : 'ON AIR'}
              </span>
            )}
          </div>

          {/* Channels */}
          <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {CHANNELS.map(ch => {
              const isActive = activeChannel?.id === ch.id;
              return (
                <button
                  key={ch.id}
                  onClick={() => selectChannel(ch)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: isActive
                      ? `1px solid ${ch.color}80`
                      : '1px solid rgba(255,255,255,0.08)',
                    backgroundColor: isActive ? `${ch.color}18` : 'rgba(255,255,255,0.03)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                    transition: 'all 0.15s',
                  }}
                >
                  {/* Play indicator */}
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    backgroundColor: isActive && playing ? ch.color : 'rgba(255,255,255,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'background-color 0.15s',
                  }}>
                    {isActive && loading ? (
                      <span style={{ width: 10, height: 10, border: `2px solid ${ch.color}`, borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                    ) : isActive && playing ? (
                      // Pause icon
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="#000">
                        <rect x="1" y="1" width="3" height="8" rx="1" />
                        <rect x="6" y="1" width="3" height="8" rx="1" />
                      </svg>
                    ) : (
                      // Play icon
                      <svg width="10" height="10" viewBox="0 0 10 10" fill={ch.color}>
                        <polygon points="2,1 9,5 2,9" />
                      </svg>
                    )}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      color: isActive ? ch.color : '#e5e7eb',
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {ch.label}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: 8, letterSpacing: '0.05em', marginTop: 1 }}>
                      {ch.sublabel}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Error */}
          {error && (
            <div style={{ padding: '4px 12px 8px', color: '#f87171', fontSize: 9, textAlign: 'center', letterSpacing: '0.08em' }}>
              {error} — tap to retry
            </div>
          )}

          {/* Volume control */}
          <div style={{
            padding: '6px 12px 10px',
            borderTop: '1px solid rgba(251,191,36,0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <button
              onClick={toggleMute}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: muted ? '#6b7280' : '#fbbf24', flexShrink: 0 }}
              title={muted ? 'Unmute' : 'Mute'}
            >
              {muted || volume === 0 ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
                </svg>
              )}
            </button>
            <input
              type="range"
              min="0" max="1" step="0.05"
              value={muted ? 0 : volume}
              onChange={handleVolumeChange}
              style={{
                flex: 1,
                height: 3,
                accentColor: '#fbbf24',
                cursor: 'pointer',
              }}
            />
            <span style={{ color: '#6b7280', fontSize: 8, letterSpacing: '0.05em', minWidth: 24, textAlign: 'right' }}>
              {muted ? 'OFF' : Math.round(volume * 100)}
            </span>
          </div>

          {/* Attribution */}
          <div style={{ padding: '0 12px 8px', color: '#374151', fontSize: 7, letterSpacing: '0.08em', textAlign: 'center' }}>
            BROADCASTIFY · PUBLIC FEEDS
          </div>
        </div>
      )}

      <style>{`
        @keyframes ping {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
